import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createBlock as _createBlock, unref as _unref, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-685501e0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "dashboard-details" }
const _hoisted_2 = { class: "dashboard-details__summary" }
const _hoisted_3 = {
  key: 0,
  class: "dashboard-details__state",
  role: "status"
}
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", {
  class: "dashboard-details__spinner",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_5 = {
  key: 1,
  class: "dashboard-details__state dashboard-details__state--error"
}
const _hoisted_6 = {
  key: 2,
  class: "dashboard-details__state"
}
const _hoisted_7 = {
  class: "dashboard-details__table-wrap",
  "data-pagination-scroll-anchor": ""
}
const _hoisted_8 = { class: "dashboard-details__table" }
const _hoisted_9 = { class: "dashboard-details__status-cell" }
const _hoisted_10 = { class: "dashboard-details__initiator-heading" }
const _hoisted_11 = { class: "dashboard-details__dates-heading" }
const _hoisted_12 = { class: "dashboard-details__actions-heading" }
const _hoisted_13 = {
  key: 0,
  class: "dashboard-details__reg-number"
}
const _hoisted_14 = { class: "dashboard-details__stacked-cell" }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = {
  key: 1,
  class: "dashboard-details__secondary"
}
const _hoisted_17 = { key: 2 }
const _hoisted_18 = { class: "dashboard-details__status-cell" }
const _hoisted_19 = { key: 1 }
const _hoisted_20 = { class: "dashboard-details__stacked-cell dashboard-details__dates" }
const _hoisted_21 = {
  key: 0,
  class: "dashboard-details__secondary"
}
const _hoisted_22 = { class: "dashboard-details__actions-cell" }
const _hoisted_23 = { class: "dashboard-details__pagination" }

import { computed, ref, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";
import { EyeIcon } from "@heroicons/vue/solid";
import Modal from "@/components/common/Modal.vue";
import TablePagination from "@/components/common/pagination/TablePagination.vue";
import DocumentStatusLabel from "@/components/documents/DocumentStatusLabel.vue";
import DashboardDataService from "@/services/dashboard/DashboardDataService";
import { DocumentStatusesEnum } from "@/store/modules/document/state";
import type {
  DashboardDetailItem,
  DashboardDetailType,
} from "@/types/dashboard/DashboardTypes";
import { formatDashboardDateTime } from "@/composables/useDashboardMain";
import { t } from "@/i18n";


export default /*#__PURE__*/_defineComponent({
  props: {
    show: null,
    title: null,
    type: null,
    templateId: null
  },
  emits: ["close"],
  setup(__props: any, { emit }: { emit: ({
  (event: "close"): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  show: boolean;
  title: string;
  type: DashboardDetailType;
  templateId?: string;
}

const PAGE_SIZE = 10;
const PAGINATION_BUTTONS = 5;





const items = ref<DashboardDetailItem[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const totalItems = ref(0);
const currentPage = ref(1);
const totalPages = ref(0);
let requestSequence = 0;

const startPage = computed(() => {
  if (totalPages.value <= PAGINATION_BUTTONS) return 1;
  return Math.max(
    1,
    Math.min(
      currentPage.value - Math.floor(PAGINATION_BUTTONS / 2),
      totalPages.value - PAGINATION_BUTTONS + 1
    )
  );
});

const endPage = computed(() =>
  Math.min(totalPages.value, startPage.value + PAGINATION_BUTTONS - 1)
);

function close() {
  requestSequence += 1;
  emit("close");
}

function itemKey(item: DashboardDetailItem): string {
  return `${props.type}-${item.detailId}`;
}

function detailRoute(item: DashboardDetailItem): RouteLocationRaw {
  if (item.processUserId) {
    return {
      name: "ProcessUserDetails",
      params: { id: String(item.processUserId) },
    };
  }
  return {
    name: "DocumentDetails",
    params: { id: String(item.documentId) },
  };
}

function documentStatus(status: string): DocumentStatusesEnum {
  return status as DocumentStatusesEnum;
}

async function loadDetails(page: number) {
  const requestId = ++requestSequence;
  loading.value = true;
  error.value = null;

  try {
    const response = await DashboardDataService.getDetails({
      type: props.type,
      page,
      size: PAGE_SIZE,
      ...(props.templateId ? { templateId: props.templateId } : {}),
    });
    if (requestId !== requestSequence) return;

    const payload = response.data;
    if (!payload.status || !payload.data) {
      throw new Error("Dashboard details response is empty");
    }

    items.value = payload.data.items ?? [];
    totalItems.value = payload.data.totalItems ?? 0;
    currentPage.value = payload.data.currentPage ?? page;
    totalPages.value = payload.data.totalPages ?? 0;
  } catch (_) {
    if (requestId !== requestSequence) return;
    items.value = [];
    totalItems.value = 0;
    totalPages.value = 0;
    error.value = t("dashboard.details.error");
  } finally {
    if (requestId === requestSequence) {
      loading.value = false;
    }
  }
}

watch(
  () => [props.show, props.type, props.templateId] as const,
  ([show]) => {
    if (show) {
      void loadDetails(1);
    } else {
      requestSequence += 1;
    }
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createBlock(Modal, {
    show: __props.show,
    title: __props.title,
    wide: true,
    onClose: close
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", null, _toDisplayString(_ctx.$t("dashboard.details.totalFound")) + ": " + _toDisplayString(totalItems.value), 1)
        ]),
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _hoisted_4,
              _createTextVNode(" " + _toDisplayString(_ctx.$t("dashboard.details.loading")), 1)
            ]))
          : (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createElementVNode("span", null, _toDisplayString(error.value), 1),
                _createElementVNode("button", {
                  type: "button",
                  class: "dashboard-details__retry",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (loadDetails(currentPage.value)))
                }, _toDisplayString(_ctx.$t("dashboard.retry")), 1)
              ]))
            : (!items.value.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.$t("dashboard.details.empty")), 1))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("table", _hoisted_8, [
                      _createElementVNode("thead", null, [
                        _createElementVNode("tr", null, [
                          _createElementVNode("th", null, _toDisplayString(_ctx.$t("dashboard.details.document")), 1),
                          _createElementVNode("th", null, _toDisplayString(_ctx.$t("dashboard.details.categoryAndProcess")), 1),
                          _createElementVNode("th", _hoisted_9, _toDisplayString(_ctx.$t("dashboard.details.status")), 1),
                          _createElementVNode("th", _hoisted_10, _toDisplayString(_ctx.$t("dashboard.details.initiator")), 1),
                          _createElementVNode("th", _hoisted_11, _toDisplayString(_ctx.$t("dashboard.details.dates")), 1),
                          _createElementVNode("th", _hoisted_12, _toDisplayString(_ctx.$t("dashboard.details.actions")), 1)
                        ])
                      ]),
                      _createElementVNode("tbody", null, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
                          return (_openBlock(), _createElementBlock("tr", {
                            key: itemKey(item)
                          }, [
                            _createElementVNode("td", null, [
                              _createVNode(_component_router_link, {
                                to: detailRoute(item),
                                class: "dashboard-details__document-link",
                                onClick: close
                              }, {
                                default: _withCtx(() => [
                                  (item.documentRegNumber)
                                    ? (_openBlock(), _createElementBlock("span", _hoisted_13, " #" + _toDisplayString(item.documentRegNumber), 1))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("span", null, _toDisplayString(item.documentTitle || _ctx.$t("dashboard.emptyValue")), 1)
                                ]),
                                _: 2
                              }, 1032, ["to"])
                            ]),
                            _createElementVNode("td", null, [
                              _createElementVNode("div", _hoisted_14, [
                                (item.categoryName)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(item.categoryName), 1))
                                  : _createCommentVNode("", true),
                                (item.processTypeLabel)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(item.processTypeLabel), 1))
                                  : _createCommentVNode("", true),
                                (!item.categoryName && !item.processTypeLabel)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(_ctx.$t("dashboard.emptyValue")), 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("td", _hoisted_18, [
                              (item.documentStatus)
                                ? (_openBlock(), _createBlock(DocumentStatusLabel, {
                                    key: 0,
                                    label: documentStatus(item.documentStatus)
                                  }, null, 8, ["label"]))
                                : (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(_ctx.$t("dashboard.emptyValue")), 1))
                            ]),
                            _createElementVNode("td", null, _toDisplayString(item.initiatorName || _ctx.$t("dashboard.emptyValue")), 1),
                            _createElementVNode("td", null, [
                              _createElementVNode("div", _hoisted_20, [
                                _createElementVNode("span", null, _toDisplayString(_ctx.$t("dashboard.details.createdAt")) + ": " + _toDisplayString(_unref(formatDashboardDateTime)(item.createdAt)), 1),
                                (item.deadline)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_21, _toDisplayString(_ctx.$t("dashboard.tasks.deadline")) + " " + _toDisplayString(_unref(formatDashboardDateTime)(item.deadline)), 1))
                                  : _createCommentVNode("", true)
                              ])
                            ]),
                            _createElementVNode("td", _hoisted_22, [
                              _createVNode(_component_router_link, {
                                to: detailRoute(item),
                                class: "dashboard-details__open",
                                "aria-label": _ctx.$t('dashboard.details.openDocument'),
                                title: _ctx.$t('dashboard.details.openDocument'),
                                onClick: close
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_unref(EyeIcon), { "aria-hidden": "true" }),
                                  _createElementVNode("span", null, _toDisplayString(_ctx.$t("dashboard.details.open")), 1)
                                ]),
                                _: 2
                              }, 1032, ["to", "aria-label", "title"])
                            ])
                          ]))
                        }), 128))
                      ])
                    ])
                  ]),
                  _createElementVNode("div", _hoisted_23, [
                    _createVNode(TablePagination, {
                      current: currentPage.value,
                      "total-pages": totalPages.value,
                      "start-page": _unref(startPage),
                      "end-page": _unref(endPage),
                      onPageChanged: loadDetails
                    }, null, 8, ["current", "total-pages", "start-page", "end-page"])
                  ])
                ], 64))
      ])
    ]),
    _: 1
  }, 8, ["show", "title"]))
}
}

})